/* eslint-disable eslint-react/prop-types -- props are typed via the generic forwardRef<HTMLElement, AiqInfoBoxProps>; the rule can't resolve generic forwardRef props */
import { forwardRef } from 'react';
import Info from '@mui/icons-material/Info';
import InfoTwoTone from '@mui/icons-material/InfoTwoTone';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';

import type { SxProps, Theme, TooltipProps } from '@mui/material';
import type { ReactElement, ReactNode } from 'react';

export interface AiqInfoBoxProps {
  /** Bold heading inside the panel. Omit for a body-only box. */
  title?: ReactNode;
  /**
   * Panel body. A `string[]` renders one line per item — that's the shape schema-driven callers carry —
   * and any other `ReactNode` renders as-is.
   */
  body: ReactNode;
  /** Leading icon — defaults to `InfoTwoTone`. Pass `null` to drop the icon column entirely. */
  icon?: ReactNode;
  /**
   * Trigger element. Defaults to a small `Info` sized in `em`, so it scales with whatever text it
   * sits next to. A custom trigger must be a single element that accepts a ref — MUI attaches the
   * hover/focus listeners to it — and must not carry a tooltip of its own, which would render a
   * second panel underneath this one. That rules out `AiqIconButton`, whose `tooltip` is mandatory:
   * for an icon-button trigger, use a plain `IconButton` with an `aria-label`.
   */
  children?: ReactElement;
  /** Describe an already-labelled trigger instead of replacing its accessible name with the panel. */
  describeChild?: boolean;
  /** Which side of the trigger the panel opens on — this is what moves the arrow. Defaults to `'top'`. */
  placement?: TooltipProps['placement'];
  /** Draw the arrow pointing back at the trigger. Defaults to `true`. */
  arrow?: boolean;
  /** Panel width cap in px. Defaults to 400 — room for a paragraph, still a readable measure. */
  maxWidth?: number;
  /**
   * Pins the panel open (or shut). Omit for the default hover / focus / tap behavior; pass it — with
   * `onOpen` / `onClose` — only when something else drives visibility, e.g. a guided tour.
   */
  open?: boolean;
  onOpen?: TooltipProps['onOpen'];
  onClose?: TooltipProps['onClose'];
  /** Resolved panel color (hex/rgba), not a palette path. Defaults to `primary.main`; the text auto-contrasts. */
  color?: string;
  /** Applied to the panel, not the trigger. */
  sx?: SxProps<Theme>;
}

/**
 * Info Box — the hover / focus / tap panel for guidance that is too long to fit in a `Tooltip`. Where a
 * tooltip is a short muted label, this is a titled paragraph on a filled `primary` panel: a leading info
 * icon, a bold title, and body copy that may run several lines.
 *
 * Reach for it when a field, metric, or control needs an explanation that would crowd the layout if it were
 * printed inline. For a one-line label, use a plain `Tooltip` instead.
 *
 * ```tsx
 * <AiqInfoBox title="Threat Intelligence" body="Ranks the threat groups most active in your sector." />
 *
 * <AiqInfoBox body={['Region of your tenant.', 'Defaults to us-east-1.']} placement="right">
 *   <AiqIconButton tooltip="About regions"><HelpOutlined fontSize="small" /></AiqIconButton>
 * </AiqInfoBox>
 * ```
 */
export const AiqInfoBox = forwardRef<HTMLElement, AiqInfoBoxProps>(function AiqInfoBox(
  {
    title,
    body,
    icon,
    children,
    describeChild = false,
    placement = 'top',
    arrow = true,
    maxWidth = 400,
    open,
    onOpen,
    onClose,
    color,
    sx
  },
  ref
) {
  const theme = useTheme();
  const panelColor = color ?? theme.palette.primary.main;
  // `primary.contrastText` is the designed pairing (and identical in both modes); only an override needs deriving.
  const textColor = color ? theme.palette.getContrastText(color) : theme.palette.primary.contrastText;

  // Two-tone inside the panel: its outline reads against the filled ground, where a solid glyph would
  // flatten into it. The trigger is the reverse case — see below.
  const panelIcon = icon === undefined ? <InfoTwoTone /> : icon;
  const hasIcon = panelIcon !== null && panelIcon !== false;
  const hasTitle = title !== undefined && title !== null;
  const lines = asLines(body);

  const panel = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: hasIcon ? 'auto minmax(0, 1fr)' : 'minmax(0, 1fr)',
        columnGap: 1.25,
        rowGap: 0.5,
        alignItems: 'start'
      }}
    >
      {hasIcon && <Box sx={{ display: 'flex', '& svg': { fontSize: 20 } }}>{panelIcon}</Box>}
      {hasTitle && (
        <Typography variant="subtitle2" color="inherit">
          {title}
        </Typography>
      )}
      {/* Pinned to the text column so the body lines up under the title instead of under the icon. */}
      <Typography
        variant="body2"
        color="inherit"
        component="div"
        sx={{ gridColumn: hasIcon ? 2 : 1, minWidth: 0, whiteSpace: lines === null ? undefined : 'pre-line' }}
      >
        {lines ?? body}
      </Typography>
    </Box>
  );

  return (
    <Tooltip
      ref={ref}
      describeChild={describeChild}
      arrow={arrow}
      placement={placement}
      title={panel}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      // A paragraph needs longer than the 1.5s default to read on touch, where there is no hover holding it open.
      leaveTouchDelay={10000}
      slotProps={{
        tooltip: {
          sx: [
            {
              maxWidth,
              bgcolor: panelColor,
              color: textColor,
              // The theme's tooltip is a bordered `background.overlay` chip; a filled panel wants neither.
              border: 'none',
              borderRadius: 1.5,
              boxShadow: 6,
              p: 1.5
            },
            ...(Array.isArray(sx) ? sx : [sx ?? false])
          ]
        },
        arrow: { sx: { color: panelColor } }
      }}
    >
      {children ?? (
        <Info
          // Filled, because at body-text size an outlined glyph thins out next to a label.
          // The tooltip title is a node, so MUI cannot derive an accessible name from it. `titleAccess`
          // renders an SVG `<title>` and drops `SvgIcon`'s default `aria-hidden`, which is how a screen
          // reader user reaches hover-only text; a plain `aria-label` on an `aria-hidden` element names nothing.
          titleAccess={flattenText([title, body])}
          fontSize="inherit"
          // Sized in `em` so the same trigger suits a dense input label and an `h5` section title.
          sx={{
            fontSize: '1.15em',
            color: 'text.secondary',
            cursor: 'help',
            flexShrink: 0,
            verticalAlign: 'text-bottom',
            // An unshrunk outlined / filled `InputLabel` (an empty field) sets `pointer-events: none` so a
            // click falls through to the input. Inherited, the icon could never be hovered. A click on it
            // still focuses the field, because the label it sits in is a `<label for>`.
            pointerEvents: 'auto'
          }}
        />
      )}
    </Tooltip>
  );
});

/**
 * A body of all strings renders as one line per item. Anything else (a node, a mixed array) renders
 * untouched, so callers passing markup keep full control.
 */
const asLines = (body: unknown): string | null => {
  if (!Array.isArray(body)) return null;

  const lines = body.filter((line): line is string => typeof line === 'string' && line.trim() !== '');

  return lines.length > 0 && lines.length === body.length ? lines.join('\n') : null;
};

/** Flattens the string parts of the panel into one accessible name; markup contributes nothing. */
const flattenText = (value: unknown): string => {
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value)) return value.map(flattenText).filter(Boolean).join(' ');

  return '';
};
